import React, { useState, useEffect, useRef } from 'react';
import './styles/Posts.css';
import Menu from '../main/menu';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  doc, 
  updateDoc, 
  deleteDoc, 
  getDocs,
  Timestamp 
} from 'firebase/firestore';
import { db, auth, getEffectiveUserId, getEffectiveUserData } from '../firebase';
import LinkedInService from './system/LinkedInService';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useCustomerSwitch } from '../contexts/CustomerSwitchContext';

const Posts = ({ onLogout }) => {
  const { t, language } = useLanguage();
  const { canCreateMorePosts, getRemainingWeeklyPosts, weeklyPostsLimit } = useSubscription();
  const { selectedCustomer, isWorkingOnBehalfOfCustomer } = useCustomerSwitch();
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month'); // 'month' eller 'week'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sharingPost, setSharingPost] = useState(null);
  const [deletingPost, setDeletingPost] = useState(null);
  const [shareResult, setShareResult] = useState({ success: null, message: null });
  const [postsThisWeek, setPostsThisWeek] = useState(0);
  const [remainingPosts, setRemainingPosts] = useState(0);
  const [draggedPost, setDraggedPost] = useState(null);
  
  // Nye states til post modal
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingTime, setEditingTime] = useState(false);
  const [newScheduledTime, setNewScheduledTime] = useState('');
  const [newScheduledDate, setNewScheduledDate] = useState('');

  // Hent LinkedIn-opslag
  useEffect(() => {
    if (!auth.currentUser) return;

    setLoading(true);
    
    // Brug getEffectiveUserId for at få den korrekte bruger-ID
    const effectiveUserId = getEffectiveUserId(selectedCustomer);
    
    if (!effectiveUserId) {
      console.error("Ingen gyldig bruger-ID fundet");
      setLoading(false);
      return;
    }
    
    console.log("Henter opslag for bruger:", effectiveUserId, selectedCustomer ? "Arbejder på vegne af kunde" : "Egen bruger");
    
    const postsRef = collection(db, 'users', effectiveUserId, 'posts');
    // Opdateret: Brug scheduledDateTime hvis det findes, ellers scheduledDate for bagudkompatibilitet
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const postsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sorter opslag efter deres planlagte tidspunkt (prioriter scheduledDateTime over scheduledDate)
      postsData.sort((a, b) => {
        const dateA = a.scheduledDateTime || a.scheduledDate || a.createdAt;
        const dateB = b.scheduledDateTime || b.scheduledDate || b.createdAt;
        
        if (!dateA || !dateB) return 0;
        return dateA.seconds - dateB.seconds;
      });
      
      console.log("Hentede opslag:", postsData);
      setPosts(postsData);
      setLoading(false);
    }, (error) => {
      console.error("Fejl ved hentning af opslag:", error);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [selectedCustomer]);

  // Tilføj en useEffect til at tælle opslag for denne uge
  useEffect(() => {
    const countThisWeeksPosts = async () => {
      try {
        // Brug getEffectiveUserId for at få den korrekte bruger-ID
        const effectiveUserId = getEffectiveUserId(selectedCustomer);
        
        if (!effectiveUserId) {
          console.error("Ingen gyldig bruger-ID fundet");
          return;
        }
        
        // Hent brugerens abonnement først
        const response = await fetch(
          `${process.env.REACT_APP_STRIPE_CHECK_SUBSCRIPTION_URL}/${effectiveUserId}?email=${encodeURIComponent(auth.currentUser.email)}`
        );
        
        if (!response.ok) {
          throw new Error('Kunne ikke hente abonnementsdata');
        }
        
        const subscriptionData = await response.json();
        
        // Beregn start og slut på denne uge
        const now = new Date();
        const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
        startOfWeek.setHours(0, 0, 0, 0);
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        // Konverter til Firestore timestamps
        const startTimestamp = Timestamp.fromDate(startOfWeek);
        const endTimestamp = Timestamp.fromDate(endOfWeek);

        // Hent alle opslag og filtrer dem manuelt da vi har flere mulige datofelter
        const postsRef = collection(db, 'users', effectiveUserId, 'posts');
        const querySnapshot = await getDocs(postsRef);
        
        let thisWeeksPosts = 0;
        querySnapshot.forEach(doc => {
          const postData = doc.data();
          // Tjek både scheduledDateTime og scheduledDate for kompatibilitet
          const postDate = postData.scheduledDateTime || postData.scheduledDate || postData.scheduledFor;
          
          if (postDate && postDate.seconds) {
            const postTimestamp = new Timestamp(postDate.seconds, postDate.nanoseconds || 0);
            if (postTimestamp.toMillis() >= startTimestamp.toMillis() && 
                postTimestamp.toMillis() <= endTimestamp.toMillis()) {
              thisWeeksPosts++;
            }
          }
        });

        setPostsThisWeek(thisWeeksPosts);
        setRemainingPosts(getRemainingWeeklyPosts(thisWeeksPosts));
        
      } catch (error) {
        console.error('Fejl ved optælling af ugens opslag:', error);
      }
    };
    
    countThisWeeksPosts();
  }, [getRemainingWeeklyPosts, selectedCustomer]);

  // Del opslag på LinkedIn
  const sharePostToLinkedIn = async (post) => {
    if (!auth.currentUser) return;
    
    try {
      setSharingPost(post.id);
      setShareResult({ success: null, message: null });
      
      // Brug getEffectiveUserId for at få den korrekte bruger-ID
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        console.error("Ingen gyldig bruger-ID fundet");
        setShareResult({
          success: false,
          message: "Kunne ikke finde gyldig bruger-ID."
        });
        return;
      }
      
      // Tjek om brugeren kan oprette flere opslag
      const canCreate = await canCreateMorePosts(postsThisWeek);
      
      if (!canCreate) {
        setShareResult({
          status: 'error',
          message: 'Du har opbrugt dine ugentlige opslag. Opgrader dit abonnement for at få flere.'
        });
        return;
      }
      
      // Hent LinkedIn token
      const linkedInToken = await LinkedInService.getLinkedInToken(effectiveUserId);
      
      if (!linkedInToken || !linkedInToken.accessToken) {
        setShareResult({ 
          success: false, 
          message: "Du er ikke forbundet til LinkedIn. Forbind din konto først." 
        });
        return;
      }
      
      console.log("Forsøger at dele opslag på LinkedIn:", post.id);
      
      // Del opslaget via Cloud Function
      const response = await fetch('https://us-central1-app-nuvik.cloudfunctions.net/shareToLinkedIn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accessToken: linkedInToken.accessToken,
          content: post.content,
          userId: effectiveUserId,
          imageId: post.imageId,
          imageUrl: post.imageUrl
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setShareResult({ success: true, message: t('posts', 'result.shareSuccess') });
        
        // Marker opslaget som delt i Firestore
        const postRef = doc(db, 'users', effectiveUserId, 'posts', post.id);
        await updateDoc(postRef, {
          status: 'published',
          publishedAt: Timestamp.now(),
          linkedInPostId: result.id
        });
        
      } else {
        const errorData = await response.json();
        setShareResult({ 
          success: false, 
          message: `${t('posts', 'result.errorPrefix')}${errorData.error || 'Ukendt fejl'}` 
        });
      }
    } catch (error) {
      console.error("Fejl ved deling af opslag:", error);
      setShareResult({ 
        success: false, 
        message: `${t('posts', 'result.errorPrefix')}${error.message}` 
      });
    } finally {
      setSharingPost(null);
      setTimeout(() => setShareResult({ success: null, message: null }), 5000);
    }
  };

  // Nulstil delingsresultat
  const resetShareResult = () => {
    setShareResult({ success: null, message: null });
  };

  // Formater tidspunkt
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString(language === 'english' ? 'en-US' : 'da-DK', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formater dato
  const formatDate = (date) => {
    let localeCode;
    
    switch(language) {
      case 'english':
        localeCode = 'en-US';
        break;
      case 'norwegian':
        localeCode = 'nb-NO';
        break;
      case 'swedish':
        localeCode = 'sv-SE';
        break;
      case 'danish':
      default:
        localeCode = 'da-DK';
    }
    
    return new Date(date).toLocaleDateString(localeCode, {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  // Tilføj deletePost funktion
  const deletePost = async (postId) => {
    if (!auth.currentUser) return;
    
    try {
      setDeletingPost(postId);
      
      // Brug getEffectiveUserId for at få den korrekte bruger-ID
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      
      if (!effectiveUserId) {
        console.error("Ingen gyldig bruger-ID fundet");
        setShareResult({
          success: false,
          message: "Kunne ikke finde gyldig bruger-ID."
        });
        return;
      }
      
      // Slet opslaget fra Firestore
      const postRef = doc(db, 'users', effectiveUserId, 'posts', postId);
      await deleteDoc(postRef);
      
      setShareResult({
        success: true,
        message: t('posts', 'result.postDeleted')
      });
    } catch (error) {
      console.error("Fejl ved sletning af opslag:", error);
      setShareResult({
        success: false,
        message: `${t('posts', 'result.errorPrefix')}${error.message}`
      });
    } finally {
      setDeletingPost(null);
    }
  };

  // Ny funktion til at opdatere opslags scheduledDateTime
  const updatePostSchedule = async (postId, newDateTime) => {
    try {
      const effectiveUserId = getEffectiveUserId(selectedCustomer);
      if (!effectiveUserId) return;

      const postRef = doc(db, 'users', effectiveUserId, 'posts', postId);
      await updateDoc(postRef, {
        scheduledDateTime: Timestamp.fromDate(newDateTime),
        updatedAt: Timestamp.now()
      });

      console.log(`Opslag ${postId} flyttet til ${newDateTime}`);
    } catch (error) {
      console.error("Fejl ved opdatering af opslag schedulering:", error);
    }
  };

  // Funktioner til post modal
  const handlePostClick = (post) => {
    setSelectedPost(post);
    setShowPostModal(true);
    
    // Forbereder redigering af tid
    if (post.scheduledDateTime || post.scheduledDate) {
      const scheduleDate = new Date((post.scheduledDateTime || post.scheduledDate).seconds * 1000);
      setNewScheduledDate(scheduleDate.toISOString().split('T')[0]);
      setNewScheduledTime(scheduleDate.toTimeString().slice(0, 5));
    }
  };

  const handleCloseModal = () => {
    setShowPostModal(false);
    setSelectedPost(null);
    setEditingTime(false);
    setNewScheduledTime('');
    setNewScheduledDate('');
  };

  const handleSaveTimeEdit = async () => {
    if (!selectedPost || !newScheduledDate || !newScheduledTime) return;
    
    try {
      const combinedDateTime = new Date(`${newScheduledDate}T${newScheduledTime}`);
      await updatePostSchedule(selectedPost.id, combinedDateTime);
      
      setShareResult({
        success: true,
        message: `Opslag flyttet til ${formatDate(combinedDateTime.getTime())} kl. ${formatTime(combinedDateTime.getTime())}`
      });
      
      handleCloseModal();
      setTimeout(() => setShareResult({ success: null, message: null }), 3000);
    } catch (error) {
      console.error("Fejl ved gemning af ny tid:", error);
      setShareResult({
        success: false,
        message: "Fejl ved opdatering af tidspunkt"
      });
    }
  };

  // Kalender hjælpefunktioner
  const getCalendarDates = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start på søndag
    
    const dates = [];
    const currentDateIterator = new Date(startDate);
    
    // Generer 6 uger (42 dage) for at dække hele måneden
    for (let i = 0; i < 42; i++) {
      dates.push(new Date(currentDateIterator));
      currentDateIterator.setDate(currentDateIterator.getDate() + 1);
    }
    
    return dates;
  };

  const getWeekDates = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay()); // Start på søndag
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    
    return dates;
  };

  const getPostsForDate = (date) => {
    const dateString = date.toDateString();
    return posts.filter(post => {
      const postDate = post.scheduledDateTime || post.scheduledDate;
      if (!postDate || !postDate.seconds) return false;
      
      const postDateObj = new Date(postDate.seconds * 1000);
      return postDateObj.toDateString() === dateString;
    });
  };

  const getPostsForDateTime = (date, hour) => {
    const dateString = date.toDateString();
    return posts.filter(post => {
      const postDate = post.scheduledDateTime || post.scheduledDate;
      if (!postDate || !postDate.seconds) return false;
      
      const postDateObj = new Date(postDate.seconds * 1000);
      return postDateObj.toDateString() === dateString && postDateObj.getHours() === hour;
    });
  };

  // Drag & Drop funktioner
  const handleDragStart = (e, post) => {
    setDraggedPost(post);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetDate, targetHour = null) => {
    e.preventDefault();
    
    if (!draggedPost) return;
    
    const newDateTime = new Date(targetDate);
    if (targetHour !== null) {
      newDateTime.setHours(targetHour, 0, 0, 0);
    } else {
      // Hvis ingen time specificeret, behold den originale tid
      const originalDate = draggedPost.scheduledDateTime || draggedPost.scheduledDate;
      if (originalDate && originalDate.seconds) {
        const originalDateTime = new Date(originalDate.seconds * 1000);
        newDateTime.setHours(originalDateTime.getHours(), originalDateTime.getMinutes(), 0, 0);
      }
    }
    
    await updatePostSchedule(draggedPost.id, newDateTime);
    setDraggedPost(null);
  };

  // Navigation
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  // Dagens dato tjek
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSameMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Tilføj en begrænsningsadvarsel i UI'et
  const renderLimitWarning = () => {
    if (remainingPosts <= 0) {
      return (
        <div className="limit-warning">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{t('posts', 'limitWarning.used', { limit: weeklyPostsLimit })}</p>
          <a href="/linkedin-poster/subscribe" className="upgrade-button">{t('posts', 'limitWarning.upgrade')}</a>
        </div>
      );
    } else if (remainingPosts <= 2) {
      return (
        <div className="limit-warning low">
          <i className="fas fa-info-circle"></i>
          <p>{t('posts', 'limitWarning.low', { remaining: remainingPosts })}</p>
        </div>
      );
    }
    return null;
  };

  // Render månedskaldender
  const renderMonthView = () => {
    const dates = getCalendarDates();
    const weekDays = [
      t('posts', 'calendar.weekDays.sunday'),
      t('posts', 'calendar.weekDays.monday'),
      t('posts', 'calendar.weekDays.tuesday'),
      t('posts', 'calendar.weekDays.wednesday'),
      t('posts', 'calendar.weekDays.thursday'),
      t('posts', 'calendar.weekDays.friday'),
      t('posts', 'calendar.weekDays.saturday')
    ];
    
    return (
      <div className="calendar-view month-view">
        <div className="calendar-header">
          <div className="calendar-nav">
            <button onClick={() => navigateMonth(-1)} className="nav-button">
              <i className="fas fa-chevron-left"></i>
            </button>
            <h2>
              {currentDate.toLocaleDateString('da-DK', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </h2>
            <button onClick={() => navigateMonth(1)} className="nav-button">
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
        
        <div className="calendar-grid">
          {/* Ugedag headers */}
          {weekDays.map(day => (
            <div key={day} className="weekday-header">
              {day}
            </div>
          ))}
          
          {/* Datoer */}
          {dates.map(date => {
            const dayPosts = getPostsForDate(date);
            return (
              <div 
                key={date.toISOString()}
                className={`calendar-day ${!isSameMonth(date) ? 'other-month' : ''} ${isToday(date) ? 'today' : ''}`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, date)}
              >
                <div className="day-number">{date.getDate()}</div>
                <div className="day-posts">
                  {dayPosts.map(post => (
                    <div
                      key={post.id}
                      className={`post-item ${post.status || 'scheduled'}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, post)}
                      onClick={(e) => {
                        // Hvis det ikke er en drag operation, åbn modalen
                        if (!draggedPost) {
                          e.stopPropagation();
                          handlePostClick(post);
                        }
                      }}
                      title={post.content.substring(0, 100) + '...'}
                    >
                      <div className="post-time">
                        {post.scheduledDateTime || post.scheduledDate ? 
                          formatTime((post.scheduledDateTime || post.scheduledDate).seconds * 1000) : 
                          t('posts', 'calendar.noTime')
                        }
                      </div>
                      <div className="post-preview">
                        {post.content.substring(0, 30)}...
                      </div>
                      <div className="post-actions-mini">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            sharePostToLinkedIn(post);
                          }}
                          disabled={sharingPost === post.id || post.status === 'published'}
                          className="share-mini"
                        >
                          <i className="fab fa-linkedin"></i>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(t('posts', 'confirmation.delete'))) {
                              deletePost(post.id);
                            }
                          }}
                          disabled={deletingPost === post.id}
                          className="delete-mini"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render ugekalender
  const renderWeekView = () => {
    const dates = getWeekDates();
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    return (
      <div className="calendar-view week-view">
        <div className="calendar-header">
          <div className="calendar-nav">
            <button onClick={() => navigateWeek(-1)} className="nav-button">
              <i className="fas fa-chevron-left"></i>
            </button>
            <h2>
              {t('posts', 'calendar.weekHeader', {
                week: Math.ceil(currentDate.getDate() / 7),
                month: currentDate.toLocaleDateString('da-DK', { month: 'long' }),
                year: currentDate.getFullYear()
              })}
            </h2>
            <button onClick={() => navigateWeek(1)} className="nav-button">
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
        
        <div className="week-grid">
          <div className="time-column">
            <div className="time-header"></div>
            {hours.map(hour => (
              <div key={hour} className="time-slot">
                {hour.toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>
          
          {dates.map(date => (
            <div key={date.toISOString()} className="day-column">
              <div className={`day-header ${isToday(date) ? 'today' : ''}`}>
                <div className="day-name">
                  {date.toLocaleDateString('da-DK', { weekday: 'short' })}
                </div>
                <div className="day-date">{date.getDate()}</div>
              </div>
              
              {hours.map(hour => {
                const hourPosts = getPostsForDateTime(date, hour);
                return (
                  <div
                    key={hour}
                    className="hour-slot"
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, date, hour)}
                  >
                    {hourPosts.map(post => (
                      <div
                        key={post.id}
                        className={`post-block ${post.status || 'scheduled'}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, post)}
                        onClick={(e) => {
                          // Hvis det ikke er en drag operation, åbn modalen
                          if (!draggedPost) {
                            e.stopPropagation();
                            handlePostClick(post);
                          }
                        }}
                      >
                        <div className="post-block-content">
                          <div className="post-block-time">
                            {post.scheduledDateTime || post.scheduledDate ? 
                              formatTime((post.scheduledDateTime || post.scheduledDate).seconds * 1000) : 
                              t('posts', 'calendar.noTime')
                            }
                          </div>
                          <div className="post-block-text">
                            {post.content.substring(0, 40)}...
                          </div>
                          <div className="post-block-actions">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                sharePostToLinkedIn(post);
                              }}
                              disabled={sharingPost === post.id || post.status === 'published'}
                              className="share-mini"
                            >
                              <i className="fab fa-linkedin"></i>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm(t('posts', 'confirmation.delete'))) {
                                  deletePost(post.id);
                                }
                              }}
                              disabled={deletingPost === post.id}
                              className="delete-mini"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-layout">
      <Menu onLogout={onLogout} />
      
      <main className="dashboard-content">
        {/* Vis banner, hvis man arbejder på vegne af kunde */}
        {isWorkingOnBehalfOfCustomer && (
          <div className="customer-action-banner">
            <i className="fas fa-user-friends"></i>
            <span>{t('posts', 'customerBanner', { customerName: selectedCustomer.name || selectedCustomer.email })}</span>
          </div>
        )}
        
        <div className="posts-header">
          <h1>{t('posts', 'header.title')}</h1>
          <div className="posts-controls">
            <div className="view-toggle">
              <button 
                className={`toggle-btn ${view === 'month' ? 'active' : ''}`}
                onClick={() => setView('month')}
              >
                <i className="fas fa-calendar-alt"></i>
                {t('posts', 'calendar.viewToggle.month')}
              </button>
              <button 
                className={`toggle-btn ${view === 'week' ? 'active' : ''}`}
                onClick={() => setView('week')}
              >
                <i className="fas fa-calendar-week"></i>
                {t('posts', 'calendar.viewToggle.week')}
              </button>
            </div>
            <div className="posts-limits-info">
              <span className="limit-badge">
                <i className="fas fa-calendar-week"></i>
                {t('posts', 'header.postsLeft', { remaining: remainingPosts, limit: weeklyPostsLimit })}
              </span>
            </div>
          </div>
        </div>
        
        {renderLimitWarning()}

        {loading ? (
          <div className="loading-posts">{t('posts', 'loading')}</div>
        ) : (
          <div className="calendar-container">
            {view === 'month' ? renderMonthView() : renderWeekView()}
          </div>
        )}

        {/* Delingsresultat notifikation */}
        {shareResult.success !== null && (
          <div className={`share-result ${shareResult.success ? 'success' : 'error'}`}>
            <p>{shareResult.message}</p>
            <button className="close-button" onClick={resetShareResult}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Post Modal */}
        {showPostModal && selectedPost && (
          <div className="post-modal-overlay" onClick={handleCloseModal}>
            <div className="post-modal" onClick={(e) => e.stopPropagation()}>
              <div className="post-modal-header">
                <h3>{t('posts', 'modal.title')}</h3>
                <button className="close-modal-btn" onClick={handleCloseModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="post-modal-content">
                <div className="post-content-section">
                  <h4>{t('posts', 'modal.sections.content')}</h4>
                  <div className="post-content-display">
                    {selectedPost.content}
                  </div>
                </div>

                <div className="post-schedule-section">
                  <h4>{t('posts', 'modal.sections.schedule')}</h4>
                  {!editingTime ? (
                    <div className="schedule-display">
                      <span className="schedule-text">
                        {selectedPost.scheduledDateTime || selectedPost.scheduledDate ? 
                          `${formatDate((selectedPost.scheduledDateTime || selectedPost.scheduledDate).seconds * 1000)} kl. ${formatTime((selectedPost.scheduledDateTime || selectedPost.scheduledDate).seconds * 1000)}` : 
                          t('posts', 'modal.schedule.noTime')
                        }
                      </span>
                      <button 
                        className="edit-time-btn"
                        onClick={() => setEditingTime(true)}
                      >
                        <i className="fas fa-edit"></i> {t('posts', 'modal.schedule.edit')}
                      </button>
                    </div>
                  ) : (
                    <div className="schedule-edit">
                      <div className="date-time-inputs">
                        <div className="input-group">
                          <label>{t('posts', 'modal.schedule.date')}</label>
                          <input 
                            type="date" 
                            value={newScheduledDate}
                            onChange={(e) => setNewScheduledDate(e.target.value)}
                          />
                        </div>
                        <div className="input-group">
                          <label>{t('posts', 'modal.schedule.time')}</label>
                          <input 
                            type="time" 
                            value={newScheduledTime}
                            onChange={(e) => setNewScheduledTime(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="edit-actions">
                        <button 
                          className="cancel-edit-btn"
                          onClick={() => setEditingTime(false)}
                        >
                          {t('posts', 'modal.schedule.cancel')}
                        </button>
                        <button 
                          className="save-edit-btn"
                          onClick={handleSaveTimeEdit}
                        >
                          {t('posts', 'modal.schedule.save')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="post-status-section">
                  <h4>{t('posts', 'modal.sections.status')}</h4>
                  <span className={`status-badge ${selectedPost.status || 'scheduled'}`}>
                    {selectedPost.status === 'published' ? t('posts', 'modal.status.published') :
                     selectedPost.status === 'ready' ? t('posts', 'modal.status.ready') :
                     selectedPost.status === 'scheduled' ? t('posts', 'modal.status.scheduled') : 
                     t('posts', 'modal.status.unknown')}
                  </span>
                </div>

                {selectedPost.imageUrl && (
                  <div className="post-image-section">
                    <h4>{t('posts', 'modal.sections.image')}</h4>
                    <img 
                      src={selectedPost.imageUrl} 
                      alt="Post billede"
                      className="post-image-preview"
                    />
                  </div>
                )}
              </div>

              <div className="post-modal-footer">
                <button 
                  className="delete-post-btn"
                  onClick={() => {
                    if (window.confirm(t('posts', 'confirmation.delete'))) {
                      deletePost(selectedPost.id);
                      handleCloseModal();
                    }
                  }}
                  disabled={deletingPost === selectedPost.id}
                >
                  <i className="fas fa-trash"></i> {t('posts', 'modal.actions.delete')}
                </button>
                
                <button 
                  className="share-post-btn"
                  onClick={() => {
                    sharePostToLinkedIn(selectedPost);
                    handleCloseModal();
                  }}
                  disabled={sharingPost === selectedPost.id || selectedPost.status === 'published'}
                >
                  <i className="fab fa-linkedin"></i> {t('posts', 'modal.actions.share')}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Posts;
